/**
 * Signature box: finger on tablets/phones, mouse on desktop (pointer events).
 *  - Drawn inline in a fixed-ratio box and, with "Ampliar", on a full-screen surface for big or
 *    complex signatures. Strokes are stored as normalized points (see signatureGeometry), so the
 *    signature keeps exactly the same shape when the box grows or shrinks.
 *  - onChange receives a transparent PNG (fixed export size) whenever the signature changes, or
 *    null when it is cleared.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Eraser, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import {
  SIGNATURE_ASPECT,
  drawStrokes,
  fitBox,
  renderStrokesToBlob,
  toNormalized,
  type SignatureStroke,
} from './signatureGeometry';

interface SignaturePadProps {
  onChange: (png: Blob | null) => void;
  disabled?: boolean;
}

interface SurfaceProps {
  strokes: SignatureStroke[];
  onCommit: (strokes: SignatureStroke[]) => void;
  disabled?: boolean;
  /** Fill the parent's height (full screen) instead of following the width only (inline) */
  fill?: boolean;
}

function SignatureSurface({ strokes, onCommit, disabled, fill }: SurfaceProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const live = useRef<SignatureStroke | null>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });

  // Box size = the available area at the signature aspect ratio
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setBox(fitBox(width, fill ? height : Infinity));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [fill]);

  const redraw = useCallback(
    (extra?: SignatureStroke | null) => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx || box.w === 0) return;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.round(box.w * ratio);
      canvas.height = Math.round(box.h * ratio);
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      drawStrokes(ctx, extra ? [...strokes, extra] : strokes, box.w, box.h);
    },
    [box, strokes],
  );

  useEffect(() => {
    redraw();
  }, [redraw]);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) =>
    toNormalized(e.clientX, e.clientY, e.currentTarget.getBoundingClientRect());

  const onDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    live.current = [point(e)];
    redraw(live.current);
  };
  const onMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!live.current) return;
    live.current.push(point(e));
    redraw(live.current);
  };
  const onUp = () => {
    if (!live.current) return;
    const finished = live.current;
    live.current = null;
    onCommit([...strokes, finished]);
  };

  return (
    <div ref={wrapRef} className={`flex w-full items-center justify-center ${fill ? 'h-full' : ''}`}>
      <canvas
        ref={canvasRef}
        style={{ width: box.w, height: box.h }}
        className="touch-none rounded-md border bg-white"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      />
    </div>
  );
}

export function SignaturePad({ onChange, disabled }: SignaturePadProps) {
  const [strokes, setStrokes] = useState<SignatureStroke[]>([]);
  const [expanded, setExpanded] = useState(false);
  const hasInk = strokes.length > 0;

  const commit = (next: SignatureStroke[]) => {
    setStrokes(next);
    void renderStrokesToBlob(next).then(onChange);
  };

  return (
    <div className="space-y-2">
      <div style={{ aspectRatio: SIGNATURE_ASPECT }} className="w-full">
        <SignatureSurface strokes={strokes} onCommit={commit} disabled={disabled} />
      </div>
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <span>{hasInk ? 'Firma registrada' : 'Firme dentro del recuadro'}</span>
        <div className="flex gap-1">
          <Button type="button" variant="ghost" size="sm" onClick={() => setExpanded(true)} disabled={disabled}>
            <Maximize2 className="mr-1 size-4" /> Ampliar
          </Button>
          <Button type="button" variant="ghost" size="sm" onClick={() => commit([])} disabled={disabled || !hasInk}>
            <Eraser className="mr-1 size-4" /> Borrar
          </Button>
        </div>
      </div>

      {/* Full screen = a nested dialog, so it also works inside other dialogs (the desk) without
          closing them, and Esc / scroll lock are handled for us */}
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="left-0 top-0 flex h-dvh max-h-none w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none p-0 data-[state=closed]:slide-out-to-left-0 data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-left-0 data-[state=open]:slide-in-from-top-0 sm:rounded-none">
          <div className="flex items-center justify-between gap-2 border-b px-4 py-3 pr-14">
            <div>
              <DialogTitle className="text-sm font-medium">Firma</DialogTitle>
              <DialogDescription className="text-xs">Firme con el dedo dentro del recuadro.</DialogDescription>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => commit([])} disabled={!hasInk}>
                <Eraser className="mr-2 size-4" /> Borrar
              </Button>
              <Button type="button" onClick={() => setExpanded(false)}>
                <Minimize2 className="mr-2 size-4" /> Listo
              </Button>
            </div>
          </div>
          <div className="min-h-0 flex-1 p-4">
            <SignatureSurface strokes={strokes} onCommit={commit} fill />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
